import React, { useState, useRef, useEffect } from 'react';
import { useUISettings } from '../../context/UISettingsContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common/Button';

export function PinModal() {
  const { 
    isPinModalOpen, 
    setIsPinModalOpen, 
    setPin, 
    verifyPin, 
    pinEnabled, 
    showNotification 
  } = useUISettings();
  const { retryLoginWithPin, requirePin, dispatch, ACTIONS } = useAuth();
  const [pin, setPinState] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState('enter');
  const [isVerificationMode, setIsVerificationMode] = useState(false);
  const inputsRef = useRef([]);
  
  // Determine mode - if requirePin is true, we're in verification mode
  const mode = requirePin ? 'verifyLogin' : (pinEnabled ? 'verify' : 'set');

  useEffect(() => {
    // Check if this is for login verification
    if (requirePin && !isVerificationMode) {
      console.log('Opening PIN modal for login verification due to requirePin flag');
      setIsVerificationMode(true);
      setIsPinModalOpen(true);
      setPinState(['', '', '', '', '', '']);
      setConfirmPin(['', '', '', '', '', '']);
      setStep('enter');
      setTimeout(() => {
        if (inputsRef.current[0]) {
          inputsRef.current[0].focus();
        }
      }, 100);
    }
    
    const handleOpenForVerification = () => {
      console.log('Opening PIN modal for login verification from event');
      setIsVerificationMode(true);
      setIsPinModalOpen(true);
      setPinState(['', '', '', '', '', '']);
      setConfirmPin(['', '', '', '', '', '']);
      setStep('enter');
      setTimeout(() => {
        if (inputsRef.current[0]) {
          inputsRef.current[0].focus();
        }
      }, 100);
    };
    
    window.addEventListener('openPinModalForVerification', handleOpenForVerification);
    
    return () => {
      window.removeEventListener('openPinModalForVerification', handleOpenForVerification);
    };
  }, [requirePin, setIsPinModalOpen, isVerificationMode]);

  // Reset modal when closed
  const handleClose = () => {
    setIsPinModalOpen(false);
    setPinState(['', '', '', '', '', '']);
    setConfirmPin(['', '', '', '', '', '']);
    setStep('enter');
    setIsVerificationMode(false);
    if (requirePin) {
      dispatch({ type: ACTIONS.SET_REQUIRE_PIN, payload: false });
    }
  };

  if (!isPinModalOpen) return null;

  const handlePinChange = (index, value, pinArray, setPinArray) => {
    if (value.length > 1) return;
    
    const newPin = [...pinArray];
    newPin[index] = value;
    setPinArray(newPin);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e, pinArray, setPinArray) => {
    if (e.key === 'Backspace' && !pinArray[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    // Login PIN verification mode
    if (isVerificationMode || requirePin) {
      const pinString = pin.join('');
      if (pinString.length === 6) {
        console.log('Submitting PIN for login verification:', pinString);
        const result = await retryLoginWithPin(pinString);
        console.log('Login verification result:', result);
        if (result.success) {
          console.log('Login successful after PIN verification');
          setIsPinModalOpen(false);
          setPinState(['', '', '', '', '', '']);
          setConfirmPin(['', '', '', '', '', '']);
          setStep('enter');
          setIsVerificationMode(false);
          dispatch({ type: ACTIONS.SET_REQUIRE_PIN, payload: false });
          showNotification('Login successful!', 'success');
        } else {
          console.error('PIN verification failed:', result.error);
          showNotification(result.error || 'Invalid PIN. Please try again.', 'error');
          setPinState(['', '', '', '', '', '']);
          setTimeout(() => {
            if (inputsRef.current[0]) {
              inputsRef.current[0].focus();
            }
          }, 100);
        }
      } else {
        showNotification('Please enter a 6-digit PIN', 'error');
      }
    } 
    // Setting a new PIN mode
    else if (!pinEnabled) {
      if (step === 'enter') {
        const pinString = pin.join('');
        if (pinString.length === 6) {
          setStep('confirm');
          setConfirmPin(['', '', '', '', '', '']);
          setTimeout(() => {
            if (inputsRef.current[0]) {
              inputsRef.current[0].focus();
            }
          }, 100);
        } else {
          showNotification('Please enter a 6-digit PIN', 'error');
        }
      } else {
        const pinString = pin.join('');
        const confirmPinString = confirmPin.join('');
        if (pinString === confirmPinString && pinString.length === 6) {
          setPin(pinString);
          setPinState(['', '', '', '', '', '']);
          setConfirmPin(['', '', '', '', '', '']);
          setStep('enter');
          setIsPinModalOpen(false);
          showNotification('PIN set successfully!', 'success');
        } else {
          showNotification('PINs do not match', 'error');
          setConfirmPin(['', '', '', '', '', '']);
          setTimeout(() => {
            if (inputsRef.current[0]) {
              inputsRef.current[0].focus();
            }
          }, 100);
        }
      }
    } 
    // Unlocking app mode (PIN already set)
    else {
      const pinString = pin.join('');
      if (pinString.length === 6) {
        const result = await verifyPin(pinString);
        if (result && result.success !== false) {
          setIsPinModalOpen(false);
          setPinState(['', '', '', '', '', '']);
          showNotification('App unlocked', 'success');
        } else {
          showNotification('Invalid PIN', 'error');
          setPinState(['', '', '', '', '', '']);
          setTimeout(() => {
            if (inputsRef.current[0]) {
              inputsRef.current[0].focus();
            }
          }, 100);
        }
      } else {
        showNotification('Please enter a 6-digit PIN', 'error');
      }
    }
  };

  const getTitle = () => {
    if (isVerificationMode || requirePin) return 'Verify PIN to Login';
    if (!pinEnabled) {
      return step === 'enter' ? 'Set PIN Code' : 'Confirm PIN Code';
    }
    return 'Enter PIN Code to Unlock';
  };

  const getDescription = () => {
    if (isVerificationMode || requirePin) {
      return 'Enter your 6-digit PIN to complete login';
    }
    if (!pinEnabled) {
      return step === 'enter' 
        ? 'Enter a 6-digit PIN to secure your app' 
        : 'Re-enter your PIN to confirm';
    }
    return 'Enter your 6-digit PIN to unlock';
  };

  const currentPin = (!pinEnabled && step === 'confirm') ? confirmPin : pin;
  const setCurrentPin = (!pinEnabled && step === 'confirm') ? setConfirmPin : setPinState;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
        <h3 className="text-xl font-bold mb-4 text-gray-800">
          {getTitle()}
        </h3>
        
        <p className="text-sm text-gray-600 mb-6">
          {getDescription()}
        </p>
        
        <div className="flex justify-center space-x-3 mb-8">
          {currentPin.map((digit, index) => (
            <input
              key={index}
              ref={el => inputsRef.current[index] = el}
              type="password"
              maxLength="1"
              value={digit}
              onChange={(e) => handlePinChange(
                index, 
                e.target.value.replace(/\D/g, ''), 
                currentPin,
                setCurrentPin
              )}
              onKeyDown={(e) => handleKeyDown(
                index, 
                e, 
                currentPin,
                setCurrentPin
              )}
              className="w-12 h-12 text-center text-2xl border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              autoFocus={index === 0}
            />
          ))}
        </div>
        
        <div className="flex justify-end space-x-4">
          <Button onClick={handleClose} variant="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleVerify}
            disabled={currentPin.join('').length !== 6}
            variant="success"
          >
            {(isVerificationMode || requirePin) ? 'Verify & Login' : 
             (!pinEnabled ? (step === 'enter' ? 'Next' : 'Confirm') : 'Unlock')}
          </Button>
        </div>
      </div>
    </div>
  );
}